import { DefinitionKind, SelectionKind } from './constants';

/**
 * Return root query object to work against based on if an operationName key
 * is set in operation.
 *
 * @param {Operation} operation
 * @return {DocumentNode}
 */
const getRootQuery = operation => {
  const {
    query,
    operationName
  } = operation; // eslint-disable-next-line no-prototype-builtins

  return query && query.hasOwnProperty(operationName) ? // @ts-ignore
  query[operationName] : query;
};
/**
 * Return the root key that will contain the data returned via the GraphQL query.
 *
 * @param {Operation} operation
 * @return {string | null}
 */


export const getRootKey = operation => {
  const definition = getRootQuery(operation).definitions.find(definition => definition.operation === 'query');

  if (definition) {
    const operationDefinition = definition;
    const selectionSet = operationDefinition.selectionSet;
    const selection = selectionSet.selections.find(selection => selection.name.kind === 'Name');

    if (selection) {
      var _name;

      return selection === null || selection === void 0 ? void 0 : (_name = selection.name) === null || _name === void 0 ? void 0 : _name.value;
    }
  }

  return null;
};
/**
 * Contentful API - Search Parameters
 * @ref https://www.contentful.com/developers/docs/references/content-delivery-api/#/reference/search-parameters
 */

const contentfulReservedParameters = ['access_token', 'id', 'include', 'locale', 'content_type', 'select', 'query', 'links_to_entry', 'links_to_asset', 'order', 'limit', 'skip', 'mimetype_group', 'preview'];
/**
 * Pass in a GraphQL query variable field and get the equivalent
 * Contentful REST API query argument.
 *
 * @ref https://www.contentful.com/developers/docs/references/content-delivery-api/#/reference/search-parameters/search-on-references
 *
 * @param {string} variableKey
 * @return {string}
 */

const getSearchKey = variableKey => {
  if (variableKey.endsWith('_in')) {
    return `fields.${variableKey.replace('_in', '')}[in]`;
  }

  if (variableKey.endsWith('_not')) {
    return `fields.${variableKey.replace('_not', '')}[ne]`;
  }

  if (variableKey.endsWith('_exists')) {
    return `fields.${variableKey.replace('_exists', '')}[exists]`;
  }

  if (variableKey.endsWith('_not_in')) {
    return `fields.${variableKey.replace('_not_in', '')}[nin]`;
  } // if (variableKey.endsWith('_contains')) {
  //   return `fields.${variableKey.replace('_contains', '')}[?]`;
  // }
  // if (variableKey.endsWith('_not_contains')) {
  //   return `fields.${variableKey.replace('_not_contains', '')}[?]`;
  // }
  // @todo Add support for `x[all]` - Ryan
  // @todo Add support for `x[match]` - Ryan
  // @todo Add support for `x[gt]` - Ryan
  // @todo Add support for `x[gte]` - Ryan
  // @todo Add support for `x[lt]` - Ryan
  // @todo Add support for `x[lte]` - Ryan
  // @todo Add support for `x[near]` - Ryan
  // @todo Add support for `x[within]` - Ryan


  return `fields.${variableKey}`;
};
/**
 * Convert `order` arguments to the expected format that works with the REST API.
 *
 * @param {string} orderValue
 * @return {string}
 */


const getOrderValue = orderValue => {
  if (!orderValue || typeof orderValue !== 'string') {
    return orderValue;
  } // Convert separators to dot notation


  orderValue = orderValue.replace(/_/g, '.'); // Prepend `fields.` for instances not prefixed by `sys.`

  if (!orderValue.startsWith('sys.')) {
    orderValue = `fields.${orderValue}`;
  } // Convert ordering direction


  if (orderValue.endsWith('.DESC')) {
    orderValue = `-${orderValue.replace('.DESC', '')}`;
  } else if (orderValue.endsWith('.ASC')) {
    orderValue = orderValue.replace('.ASC', '');
  } // Convert sys date fields to REST equivalents


  if (orderValue.includes('sys.firstPublishedAt')) {
    orderValue = orderValue.replace('sys.firstPublishedAt', 'sys.createdAt');
  }

  if (orderValue.includes('sys.publishedAt')) {
    orderValue = orderValue.replace('sys.publishedAt', 'sys.updatedAt');
  }

  return orderValue;
};
/**
 * GraphQL queries specify the expected shape of the response from the API,
 * so this method attempts to extract that in a format that we can apply
 * to the parsed REST API response, in order to make sure the response
 * fulfills the contract of the request.
 *
 * @param {SelectionSetNode} selectionSet
 * @param {ReadonlyArray<DefinitionNode>} definitions
 * @return {DefinitionMap}
 */


const extractSelections = (selectionSet, definitions) => {
  if (!(selectionSet && selectionSet.selections && selectionSet.selections.length)) {
    return null;
  }

  const selections = {};
  selectionSet.selections.forEach(selection => {
    if (selection.kind === SelectionKind.Field) {
      var _selectionFieldNode$n;

      const selectionFieldNode = selection;

      if (selectionFieldNode !== null && selectionFieldNode !== void 0 && (_selectionFieldNode$n = selectionFieldNode.name) !== null && _selectionFieldNode$n !== void 0 && _selectionFieldNode$n.value) {
        var _selectionFieldNode$n2;

        if (selectionFieldNode.selectionSet && selectionFieldNode !== null && selectionFieldNode !== void 0 && (_selectionFieldNode$n2 = selectionFieldNode.name) !== null && _selectionFieldNode$n2 !== void 0 && _selectionFieldNode$n2.value) {
          selections[selectionFieldNode.name.value] = extractSelections(selectionFieldNode.selectionSet, definitions);
        }
      }
    } else if (selection.kind === SelectionKind.FragmentSpread) {
      const selectionFragmentSpreadNode = selection;
      const fragmentDefinition = definitions.find(definition => definition.kind === DefinitionKind.FramentDefinition && definition.name && definition.name.value && selectionFragmentSpreadNode.name && selectionFragmentSpreadNode.name.value && definition.name.value === selectionFragmentSpreadNode.name.value);

      if (fragmentDefinition) {
        selections[`...${selectionFragmentSpreadNode.name.value}`] = extractSelections(fragmentDefinition.selectionSet, definitions);
      }
    }
  });
  return selections;
};
/**
 *
 *
 * @param {Operation} operation
 * @return {DefinitionMap}
 */


export const buildDefinitionMap = operation => {
  const {
    query
  } = operation;
  const operationDefinition = query.definitions.find(definition => definition.kind === DefinitionKind.OperationDefinition);

  if (!(operationDefinition && operationDefinition.name && operationDefinition.name.value)) {
    return {};
  }

  return {
    [operationDefinition.name.value]: extractSelections(operationDefinition.selectionSet, query.definitions)
  };
};
/**
 * Convert the query variables to a format the Contentful API understands
 *
 * @param {Object} operation
 * @retrun {Object}
 */

export const parseQueryVariables = operation => {
  var _operationDefinition$;

  const {
    variables
  } = operation;
  const rootQuery = getRootQuery(operation);
  const rootQueryDefinitions = rootQuery.definitions;

  if (!rootQueryDefinitions) {
    return {};
  }

  const operationDefinition = rootQueryDefinitions.find(definition => definition.kind === DefinitionKind.OperationDefinition);

  if (!operationDefinition) {
    return {};
  }

  const selectionSetSelection = operationDefinition === null || operationDefinition === void 0 ? void 0 : (_operationDefinition$ = operationDefinition.selectionSet) === null || _operationDefinition$ === void 0 ? void 0 : _operationDefinition$.selections[0];

  if (!selectionSetSelection) {
    return {};
  }

  return ((selectionSetSelection === null || selectionSetSelection === void 0 ? void 0 : selectionSetSelection.arguments) || []).map(argument => {
    var _argument$value, _argument$value$value, _argument$value2, _argument$value3, _name2;

    const argumentKey = contentfulReservedParameters.includes(argument.name.value) ? argument.name.value : getSearchKey(argument.name.value);

    switch (argument === null || argument === void 0 ? void 0 : (_argument$value = argument.value) === null || _argument$value === void 0 ? void 0 : _argument$value.kind) {
      case 'ListValue':
        return {
          [argumentKey]: ((_argument$value$value = argument === null || argument === void 0 ? void 0 : (_argument$value2 = argument.value) === null || _argument$value2 === void 0 ? void 0 : _argument$value2.values) !== null && _argument$value$value !== void 0 ? _argument$value$value : []).map(argumentValue => {
            switch (argument.name.value) {
              case 'order':
                return getOrderValue(argumentValue.value);

              default:
                return argumentValue === null || argumentValue === void 0 ? void 0 : argumentValue.value;
            }
          }).join(',')
        };

      case 'BooleanValue':
      case 'EnumValue':
      case 'FloatValue':
      case 'IntValue':
      case 'StringValue':
        return {
          [argumentKey]: argument === null || argument === void 0 ? void 0 : (_argument$value3 = argument.value) === null || _argument$value3 === void 0 ? void 0 : _argument$value3.value
        };

      case 'Variable':
        return {
          [argumentKey]: variables[(_name2 = (argument === null || argument === void 0 ? void 0 : argument.value).name) === null || _name2 === void 0 ? void 0 : _name2.value]
        };

      case 'ObjectValue':
        return (argument.value.fields || []).map(objectField => {
          const objectFieldKey = contentfulReservedParameters.includes(objectField.name.value) ? objectField.name.value : getSearchKey(objectField.name.value);

          switch (objectField.value.kind) {
            case 'Variable':
              return {
                [objectFieldKey]: variables[objectField.value.name.value]
              };

            case 'ListValue':
              return {
                [objectFieldKey]: objectField.value.values.map(objectFieldValue => objectFieldValue.value).join(',')
              };

            case 'BooleanValue':
            case 'EnumValue':
            case 'IntValue':
            case 'FloatValue':
            case 'StringValue':
              return {
                [objectFieldKey]: objectField.value.value
              };

            case 'NullValue':
              return {
                [objectFieldKey]: null
              };

            default:
              return null;
          }
        }).filter(item => !!item).reduce((acc, cur) => {
          return { ...acc,
            ...cur
          };
        }, {});

      default:
        return null;
    }
  }).filter(item => !!item).reduce((acc, cur) => {
    return { ...acc,
      ...cur
    };
  }, {});
};